/**
 * Cliente HTTP de Conversaciones del WhatsApp oficial (spec de conversaciones §15.4 y §17): mismo camino
 * que `api.ts` y `estadisticasApi.ts` (`req()` de campana-nueva y `qs`), con el mismo criterio: nunca se
 * manda `brandId` (el tenant sale del token) y los ids van codificados.
 *
 * Archivo aparte de `api.ts` porque ése lo tocan otros frentes: acá vive sólo lo que cuelga de
 * `/conversaciones` (más la simulación de un entrante, que en la bandeja manda campos nuevos).
 *
 * Ojo con los 422: `req()` los DEVUELVE sin lanzar. Toda acción de la bandeja pasa por `llamar()`
 * (`components/base/comun.tsx`), que junta lo devuelto y lo lanzado en una sola `Lectura`.
 */

import { req, reqBlob } from "../campana-nueva/api";
import { qs } from "./api";
import type { Resp } from "./api";
import type { SimularInboundResultado } from "./types";
import type * as C from "./types.conversaciones";

const B = "/activa/oficial/conversaciones";
const e = (id: string) => encodeURIComponent(id);

/**
 * El cuerpo de `POST /simulate-inbound` (§4.5). Vive acá y no en `types.conversaciones.ts` porque ése es
 * copia exacta del backend y lo custodia un test de contrato. `from` no hace falta con `sinTelefono`.
 */
export interface SimularEntranteBody {
  from?: string;
  body?: string;
  buttonPayload?: string;
  buttonText?: string;
  profileName?: string;
  sinTelefono?: boolean;
  externalUserId?: string;
  medios?: { contentType: string }[];
  ubicacion?: { latitud: number; longitud: number; direccion?: string; etiqueta?: string };
}

export const conversacionesApi = {
  listar: (f: { filtro?: C.FiltroConversaciones; campana?: string; q?: string; page?: number; limit?: number }): Promise<Resp<C.PaginaConversaciones>> =>
    req("GET", `${B}${qs(f)}`),
  contador: (): Promise<Resp<C.ContadorConversaciones>> => req("GET", `${B}/contador`),
  /** `antesDe` es la `clave` de un ítem (el `cursorAnteriores` de la página anterior), tal cual. */
  obtener: (id: string, o?: { antesDe?: string; limite?: number }): Promise<Resp<C.Hilo>> => req("GET", `${B}/${e(id)}${qs(o || {})}`),
  responder: (id: string, b: { texto: string; claveCliente: string; vistoHasta: string; confirmarDuplicado?: boolean }): Promise<Resp<C.EnvioManualPublico>> =>
    req("POST", `${B}/${e(id)}/responder`, b),
  opcionesReapertura: (id: string): Promise<Resp<C.OpcionesReapertura>> => req("GET", `${B}/${e(id)}/reabrir`),
  reabrir: (id: string, b: { plantillaId: string; variables: Record<string, string>; claveCliente: string; vistoHasta: string; confirmarDuplicado?: boolean }): Promise<Resp<C.EnvioManualPublico>> =>
    req("POST", `${B}/${e(id)}/reabrir`, b),
  cambiarEstado: (id: string, estado: "resuelta" | "sin_responder"): Promise<Resp<C.ConversacionDetalle>> => req("POST", `${B}/${e(id)}/estado`, { estado }),
  /** Manda `{}` y no un cuerpo vacío: `req()` omite un cuerpo falsy y algunos parsers dan 400 con JSON vacío. */
  resolverVencidas: (): Promise<Resp<C.ResultadoResolverVencidas>> => req("POST", `${B}/resolver-vencidas`, {}),
  darDeBaja: (id: string, motivo: string): Promise<Resp<C.ConversacionDetalle>> => req("POST", `${B}/${e(id)}/baja`, { motivo }),
  suprimir: (id: string, b: { motivo: string; dejarEnNoContactar: boolean }): Promise<Resp<C.ResultadoSupresionConversacion>> =>
    req("POST", `${B}/${e(id)}/suprimir`, b),
  exportar: (id: string): Promise<Resp<C.ExportacionConversacion>> => req("GET", `${B}/${e(id)}/exportar`),
  porContacto: (contactoId: string): Promise<Resp<{ conversacionId: string | null }>> => req("GET", `${B}/por-contacto/${e(contactoId)}`),
  /** `ruta` es la que manda el backend en cada medio del hilo (`MedioHilo.ruta`): se usa tal cual. */
  medio: (ruta: string): Promise<Blob> => reqBlob(ruta),
  /** Sólo en simulado (en twilio el backend responde 409 SOLO_SIMULADO). */
  simularEntrante: (b: SimularEntranteBody): Promise<Resp<SimularInboundResultado>> => req("POST", "/activa/oficial/simulate-inbound", b),
  simularVencerVentana: (id: string): Promise<Resp<{ ultimoEntranteAt: string }>> => req("POST", `${B}/${e(id)}/simular/vencer-ventana`, {}),
};
